import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Form, Select, Input, Button, Space, Tag, List, message, Popconfirm } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const CustomAudienceBuilder = () => {
  const [form] = Form.useForm();
  const [rules, setRules] = useState([]);
  const [name, setName] = useState('');
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { token } = useAuth();

  const addRule = () => {
    const values = form.getFieldsValue();
    const rule = {
      id: Math.random(),
      field: values.field,
      operator: values.operator,
      value: values.value,
    };
    if (!rule.field || !rule.operator || !rule.value) return message.warning('Complete the rule fields');
    setRules(prev => [rule, ...prev]);
    form.resetFields(['field', 'operator', 'value']);
  };

  const removeRule = (id) => setRules(prev => prev.filter(r => r.id !== id));

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const res = await fetch('/api/custom-audiences', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load audiences');
        const data = await res.json();
        setAudiences(Array.isArray(data) ? data : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load audiences');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onSave = async () => {
    try {
      if (!token) throw new Error('Not authenticated');
      if (!name) return message.warning('Please provide a name');
      if (rules.length === 0) return message.warning('Add at least one rule');
      setSaving(true);
      const payload = { name, rules };
      if (editingId) {
        const res = await fetch(`/api/custom-audiences/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to update audience');
        const updated = await res.json();
        setAudiences(prev => prev.map(a => a.id === updated.id ? updated : a));
        message.success('Audience updated');
      } else {
        const res = await fetch('/api/custom-audiences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to create audience');
        const created = await res.json();
        setAudiences(prev => [created, ...prev]);
        message.success('Audience saved');
      }
      setEditingId(null);
    } catch (e) {
      message.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onEditAudience = (aud) => {
    setEditingId(aud.id);
    setName(aud.name || '');
    setRules(Array.isArray(aud.rules) ? aud.rules : []);
    form.resetFields();
  };

  const onDeleteAudience = async (aud) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/custom-audiences/${aud.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete audience');
      setAudiences(prev => prev.filter(a => a.id !== aud.id));
      if (editingId === aud.id) {
        setEditingId(null);
        setName('');
        setRules([]);
        form.resetFields();
      }
      message.success('Audience deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Audience' }, { title: 'Builder' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Custom Audience Builder</Title>
        <Space>
          <Input placeholder="Audience name" value={name} onChange={(e) => setName(e.target.value)} style={{ width: 260 }} />
          <Button type="primary" onClick={onSave} loading={saving}>{editingId ? 'Update Audience' : 'Save Audience'}</Button>
        </Space>
      </div>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card title="Define Rules">
            <Paragraph type="secondary">Combine filters to build your audience.</Paragraph>
            <Form layout="vertical" form={form}>
              <Form.Item label="Field" name="field">
                <Select placeholder="Select field">
                  <Option value="purchase_count">Purchase Count</Option>
                  <Option value="last_purchase_days">Days Since Last Purchase</Option>
                  <Option value="city">City</Option>
                  <Option value="device">Device Type</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Operator" name="operator">
                <Select placeholder="Select operator">
                  <Option value=">">Greater Than</Option>
                  <Option value="<">Less Than</Option>
                  <Option value="=">Equals</Option>
                  <Option value="contains">Contains</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Value" name="value">
                <Input placeholder="Enter value" />
              </Form.Item>
              <Button onClick={addRule}>Add Rule</Button>
            </Form>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Rules Preview" extra={<span>{rules.length} rule(s)</span>}>
            <List
              dataSource={rules}
              locale={{ emptyText: 'No rules added yet' }}
              renderItem={(r) => (
                <List.Item actions={[<Button size="small" danger onClick={() => removeRule(r.id)}>Remove</Button>] }>
                  <Space wrap>
                    <Tag color="blue">{r.field}</Tag>
                    <Tag color="purple">{r.operator}</Tag>
                    <Tag>{r.value}</Tag>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
          <Card title="Saved Audiences" style={{ marginTop: 16 }} loading={loading}>
            <List
              dataSource={audiences}
              locale={{ emptyText: 'No saved audiences' }}
              renderItem={(a) => (
                <List.Item
                  actions={[
                    <Button key="edit" size="small" onClick={() => onEditAudience(a)}>Edit</Button>,
                    <Popconfirm key="del" title="Delete this audience?" onConfirm={() => onDeleteAudience(a)} okText="Delete" okButtonProps={{ danger: true }}>
                      <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    title={a.name}
                    description={<span><Tag>rules: {Array.isArray(a.rules) ? a.rules.length : 0}</Tag></span>}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CustomAudienceBuilder;
